import { StyleObject } from 'styletron-react';
import { SidebarItemProps } from '.';
import { focusRingOutline } from '../../Themes/base/focusRing';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { TSize } from './types';

const isPresetSidebarSize = (size: TSize) => size ? ['large', 'default', 'compact', 'mini'].includes(size) : false;

export const SidebarSizes: {
	[key: string]: {
		fontSize: string;
		lineHeight: string;
		iconSize: string;
		paddingTop: string,
		paddingBottom: string;
		paddingRight: string,
		paddingLeft: string
	}
} = {
	//TODO these are not being implemented when rendering
	large: {
		fontSize: 'var(--epods--sidebar-large-font-size)',
		lineHeight: 'var(--epods--sidebar-large-line-height-size)',
		iconSize: 'var(--epods--sidebar-large-icon-size)',
		paddingTop: 'var(--epods--sidebar-large-vertical-padding)',
		paddingBottom: 'var(--epods--sidebar-large-vertical-padding)',
		paddingRight: 'var(--epods--sidebar-large-horizontal-padding)',
		paddingLeft: 'var(--epods--sidebar-large-horizontal-padding)',
	},
	default: {
		fontSize: 'var(--epods--sidebar-default-font-size)',
		lineHeight: 'var(--epods--sidebar-default-line-height-size)',
		iconSize: 'var(--epods--sidebar-default-icon-size)',
		paddingTop: 'var(--epods--sidebar-default-vertical-padding)',
		paddingBottom: 'var(--epods--sidebar-default-vertical-padding)',
		paddingRight: 'var(--epods--sidebar-default-horizontal-padding)',
		paddingLeft: 'var(--epods--sidebar-default-horizontal-padding)',
	},
	compact: {
		fontSize: 'var(--epods--sidebar-compact-font-size)',
		lineHeight: 'var(--epods--sidebar-compact-line-height-size)',
		iconSize: 'var(--epods--sidebar-compact-icon-size)',
		paddingTop: 'var(--epods--sidebar-compact-vertical-padding)',
		paddingBottom: 'var(--epods--sidebar-compact-vertical-padding)',
		paddingRight: 'var(--epods--sidebar-compact-horizontal-padding)',
		paddingLeft: 'var(--epods--sidebar-compact-horizontal-padding)',
	},
	mini: {
		fontSize: 'var(--epods--sidebar-mini-font-size)',
		lineHeight: 'var(--epods--sidebar-mini-line-height-size)',
		iconSize: 'var(--epods--sidebar-mini-icon-size)',
		paddingTop: 'var(--epods--sidebar-mini-vertical-padding)',
		paddingBottom: 'var(--epods--sidebar-mini-vertical-padding)',
		paddingRight: 'var(--epods--sidebar-mini-horizontal-padding)',
		paddingLeft: 'var(--epods--sidebar-mini-horizontal-padding)',
	},
};

const defaultValues = {
	fontSize: 'var(--epods--font-size-h5)',
	lineHeight: 'var(--epods--line-height-h5)',
	paddingTop: 'var(--epods--space-inset-s)',
	paddingBottom: 'var(--epods--space-inset-s)',
	paddingRight: 'var(--epods--space-inset-s)',
	paddingLeft: 'var(--epods--space-inset-s)',
}

const getSize = (size: TSize) => size && isPresetSidebarSize(size) ? SidebarSizes[size] : defaultValues;

export function itemStyles({ isactive, size }: SidebarItemProps & { $theme: typeof Theme }): StyleObject {
	return {
		position: 'relative',
		// Override button default styles
		borderRadius: 0,
		borderLeft: 0,
		borderTop: 0,
		borderBottom: 0,
		textAlign: 'left',

		width: '100%', //TODO: review if it is really needed :: icononly === 'true' ? undefined : '100%',

		borderRightWidth: '0px', //'5px',

		//paddingRight: 'var(--epods--space-inset-m)', //icononly === 'true' ? 'var(--epods--space-inset-l)' : $theme.sizing.scale700,
		//paddingLeft: 'var(--epods--space-inset-m)',

		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: isactive === 'true' ? 'var(--epods--color-bg-toggle-active)' : 'var(--epods--color-bg-tertiary)',
		boxShadow: isactive === 'true' ? '-5px 0px 0px 0px var(--epods--color-bg-primary) inset' : 'none',
		color: isactive === 'true' ? 'var(--epods--color-text-button-tertiary)' : 'var(--epods--color-text-button-tertiary)',
		fontFamily: 'var(--epods--font-base-semibold)',
		fontWeight: 'normal',
		textDecoration: 'none',

		...getSize(size),

		':hover': {
			textDecoration: 'none',
			color: isactive === 'true' ? 'var(--epods--color-text-button-tertiary)' : 'var(--epods--color-text-button-tertiary-hover)',
			boxShadow: isactive === 'true' ? '-5px 0px 0px 0px var(--epods--color-bg-primary) inset' : 'none',
			backgroundColor: 'var(--epods--color-bg-toggle-hover)',
		},
		':focus': {
			...focusRingOutline,
			outlineOffset: '-2px',
			borderRadius: 'var(--epods--border-radius)'
		},
	};
}

export const STYLES: Record<string, StyleObject> = {
	CONTAINER: {
		display: 'flex',
		height: '100%',
		flexDirection: 'column',
		backgroundColor: 'var(--epods--color-bg)',
	},
	SCROLLABLE_CONTAINER: {
		display: 'flex',
		flexDirection: 'column',
		overflowY: 'auto',
	},
	GROUP_LIST: {
		// unstyled list
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	GROUP: {
		backgroundColor: 'var(--epods--color-bg)',
		// trick to make "fake" borders collapse
		marginTop: '1px',
		//outline: `1px solid var(--epods--color-border-light)`,
	},
	LAST_GROUP: {
		marginTop: 'auto',
	},
	ICON: {
		width: 'var(--epods--icon-small)',

		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'inherit',
	},
	ICON_WITH_TEXT: {
		marginRight: 'var(--epods--space-inset-s)',
	},
	LABEL: {
		// text overflow ellipsis
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflowX: 'hidden',
		fontFamily: 'inherit',
		color: 'inherit',
	},
	COLLAPSE_BUTTON_CONTAINER: {
		// trick to make "fake" borders collapse
		marginTop: '1px',
		//outline: `1px solid var(--epods--color-border-light)`,
	},
};
